import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Download, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TableRowsSkeleton } from "../skeleton/Skeleton";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { RootState } from "../../app/store";
import {
  selectContracts,
  selectContractsStatus,
} from "../../features/contracts/contracts-slice";
import { fetchAllContractsForExport } from "../../api/clients/billing-export-client";
import { exportToExcel } from "../../features/billing/utils/excelExport";
import PagerControls from "./PagerControls";
import {
  fetchContracts,
  holdContract,
  resumeContract,
  cancelContract,
  requestContractAcceptance,
} from "../../features/contracts/slice-extensions/contracts-slice-extension";
import { fetchPaymentTypes } from "../../features/payment-types/slice-extensions/payment-types-slice-extension";
import { selectPaymentTypes } from "../../features/payment-types/payment-types-slice";
import { Contract } from "../../features/contracts/models/Contract";
import { ContractType } from "../../features/contracts/models/ContractType";
import { formatMoney } from "../../features/billing/utils/money";
import { contractTypeLabel } from "../../features/payments/utils/paymentLabels";
import { useMemberNameLookup } from "../../features/members/hooks/useMemberNameLookup";
import { LoadingStatus } from "../../features/sessions/models/LoadingStatus";
import ContractForm from "./ContractForm";
import { PER_VISIT_ACCOUNT_EXISTS } from "../../features/contracts/models/ContractErrorCodes";
import { showErrorNotification } from "../../utils/notifications";
import FormModal from "./FormModal";
import NoteModal from "./NoteModal";
import MemberDirectoryWarning from "./MemberDirectoryWarning";
import PaymentForm from "./PaymentForm";
import ContractStatusBadge from "./ContractStatusBadge";
import ContractAcceptanceChip from "./ContractAcceptanceChip";
import ContractRowActions from "./ContractRowActions";
import { ROUTES } from "../../routes";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";

const PAGE_SIZE = 20;

/** Contracts management: list, create, hold / resume / cancel. */
const BillingContractsTab: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const contracts = useAppSelector(selectContracts);
  const status = useAppSelector(selectContractsStatus);
  const page = useAppSelector((s: RootState) => s.contracts.page);
  const totalCount = useAppSelector((s: RootState) => s.contracts.totalCount);
  const paymentTypes = useAppSelector(selectPaymentTypes);
  const memberName = useMemberNameLookup();

  const [searchParams, setSearchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Contract | null>(null);
  const [cancelling, setCancelling] = useState<Contract | null>(null);
  const [payingFor, setPayingFor] = useState<Contract | null>(null);
  const [acceptanceError, setAcceptanceError] = useState<string | null>(null);

  const requestAcceptance = async (contract: Contract) => {
    setAcceptanceError(null);
    try {
      await dispatch(requestContractAcceptance(contract.id)).unwrap();
    } catch (e) {
      setAcceptanceError(
        (e as { message?: string })?.message ??
          t("billing.acceptance.requestFailed", "The acceptance request could not be sent."),
      );
    }
  };

  /** Contract → Payments navigation: switch to the payments tab filtered by this contract. */
  const showPayments = (contractId: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", "payments");
    params.set("contractId", contractId);
    setSearchParams(params);
  };

  const load = (toPage: number) => dispatch(fetchContracts({ page: toPage, pageSize: PAGE_SIZE }));
  const reload = () => load(page);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    load(1);
    dispatch(fetchPaymentTypes({ includeArchived: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const paymentTypeName = (id: string) =>
    paymentTypes.find((pt) => pt.id === id)?.name ?? "—";

  const describeTerms = (c: Contract) => {
    if (c.type === ContractType.VisitPack) {
      return c.packSize != null
        ? t("billing.common.visitsCount", { count: c.packSize })
        : "—";
    }
    const fee = c.monthlyFeeAmountMinorUnits != null && c.monthlyFeeCurrency
      ? t("billing.common.perMonth", { amount: formatMoney(c.monthlyFeeAmountMinorUnits, c.monthlyFeeCurrency, getDisplayLocale()) })
      : "—";
    return c.cadence ? `${fee} · ${c.cadence}` : fee;
  };

  const handleExport = async () => {
    const all = await fetchAllContractsForExport();
    exportToExcel(
      "contracts",
      all.map((c) => ({
        [t("billing.table.member")]: memberName(c.memberId),
        [t("billing.table.contract")]: contractTypeLabel(c.type, t),
        [t("billing.table.terms")]: describeTerms(c),
        [t("billing.table.defaultPrice")]: paymentTypeName(c.defaultPaymentTypeId),
        [t("billing.table.validFrom")]: c.validFrom,
        [t("billing.table.validUntil")]: c.validUntil ?? "",
        [t("billing.table.status")]: t(`billing.contractStatus.${c.status}`, { defaultValue: c.status }),
        [t("billing.table.notes")]: c.notes ?? "",
      })),
    );
  };

  return (
    <div className="mt-4 space-y-6">
      <MemberDirectoryWarning />
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="btn btn-primary" onClick={() => setShowForm(true)}>
          <Plus size={18} strokeWidth={2} />
          <span>{t("billing.actions.newContract")}</span>
        </button>
        <button type="button" className="btn btn-secondary ml-auto" onClick={handleExport}>
          <Download size={16} />
          <span>{t("billing.common.exportToExcel")}</span>
        </button>
      </div>

      {acceptanceError && (
        <p className="text-sm font-medium text-red-700 dark:text-red-400">{acceptanceError}</p>
      )}

      {(showForm || editing) && (
        <FormModal onClose={() => { setShowForm(false); setEditing(null); }}>
          <ContractForm
            key={editing?.id ?? "new"}
            contract={editing ?? undefined}
            onSaved={() => { setShowForm(false); setEditing(null); reload(); }}
            onClose={() => { setShowForm(false); setEditing(null); }}
          />
        </FormModal>
      )}

      {status === LoadingStatus.Loading && contracts.length === 0 ? (
        <TableRowsSkeleton rows={5} />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-grey-90 dark:border-primary-20">
          <table className="w-full text-sm">
            <thead className="bg-grey-95 text-xs uppercase tracking-wide text-grey-30 dark:bg-primary-20 dark:text-grey-70">
              <tr>
                <th className="px-4 py-3 text-left font-medium">{t("billing.table.member")}</th>
                <th className="px-4 py-3 text-left font-medium">{t("billing.table.contract")}</th>
                <th className="px-4 py-3 text-left font-medium">{t("billing.table.defaultPrice")}</th>
                <th className="px-4 py-3 text-left font-medium">{t("billing.table.valid")}</th>
                <th className="px-4 py-3 text-left font-medium">{t("billing.table.status")}</th>
                <th className="px-4 py-3 text-right font-medium"><span className="sr-only">{t("billing.table.actions")}</span></th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-primary-10 text-grey-20 dark:text-white">
              {contracts.map((c) => {
                const name = memberName(c.memberId);
                const initials = name.split(" ").map((w) => w.charAt(0)).join("").slice(0, 2).toUpperCase();
                return (
                <tr
                  key={c.id}
                  onClick={() => setEditing(c)}
                  className="cursor-pointer border-t border-grey-90 dark:border-primary-20 hover:bg-grey-95 dark:hover:bg-primary-20"
                >
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <Link
                      to={ROUTES.MEMBER_DETAILS.replace(":id", c.memberId)}
                      className="group inline-flex items-center gap-2"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-90 text-xs font-bold text-primary-30 dark:bg-primary-30 dark:text-primary-90">
                        {initials}
                      </span>
                      <span className="font-medium text-grey-10 group-hover:underline dark:text-grey-90">
                        {name}
                      </span>
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-grey-10 dark:text-grey-90">{contractTypeLabel(c.type, t)}</p>
                    <p className="mt-0.5 text-xs text-grey-30 dark:text-grey-70">{describeTerms(c)}</p>
                  </td>
                  <td className="px-4 py-3 text-grey-30 dark:text-grey-70">{paymentTypeName(c.defaultPaymentTypeId)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-grey-30 dark:text-grey-70">
                    {c.validFrom}{c.validUntil ? ` → ${c.validUntil}` : ""}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col items-start gap-1">
                      <ContractStatusBadge status={c.status} />
                      <ContractAcceptanceChip contract={c} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <ContractRowActions
                      contract={c}
                      onAddPayment={() => setPayingFor(c)}
                      onShowPayments={() => showPayments(c.id)}
                      onEdit={() => setEditing(c)}
                      onHold={async () => { await dispatch(holdContract(c.id)); reload(); }}
                      onResume={async () => {
                        const result = await dispatch(resumeContract(c.id));
                        if (resumeContract.rejected.match(result) && result.payload?.code === PER_VISIT_ACCOUNT_EXISTS) {
                          showErrorNotification(t("billing.contractForm.errorPerVisitAccountExists"));
                        }
                        reload();
                      }}
                      onCancel={() => setCancelling(c)}
                      onRequestAcceptance={() => requestAcceptance(c)}
                    />
                  </td>
                </tr>
                );
              })}
              {contracts.length === 0 && (
                <tr>
                  <td className="px-4 py-6 text-center text-grey-40 dark:text-grey-80" colSpan={6}>
                    {t("billing.contracts.empty")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {payingFor && (
        <FormModal onClose={() => setPayingFor(null)}>
          <PaymentForm
            mode="pack"
            initialContractId={payingFor.id}
            onSaved={() => setPayingFor(null)}
            onClose={() => setPayingFor(null)}
          />
        </FormModal>
      )}

      <PagerControls page={page} totalPages={totalPages} onPageChange={load} />

      {cancelling && (
        <NoteModal
          title={t("billing.contracts.cancelTitle")}
          description={t("billing.contracts.cancelDescription", {
            member: memberName(cancelling.memberId),
            type: contractTypeLabel(cancelling.type, t),
          })}
          confirmLabel={t("billing.actions.cancelContract")}
          onCancel={() => setCancelling(null)}
          onConfirm={async (note) => {
            await dispatch(cancelContract({ id: cancelling.id, request: { reason: note || undefined } }));
            setCancelling(null);
            reload();
          }}
        />
      )}
    </div>
  );
};

export default BillingContractsTab;
